import { Badge, Box, P, RadioButton } from '..';
import { useCallback, useContext, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { ImageArea, ImageAreaProps } from './ImageArea';
import { RadioGroupContext } from '../RadioButton/RadioGroup';

type SelectableComponent = { id: string; selected?: boolean; onClick?: (value: string) => void };

export interface ImageSelectProps extends Partial<ImageAreaProps>, SelectableComponent, IntrinsicProps {
  templateName: string;
  createdAt: string;
  badge?: string;
}

const Border = styled.div<{ selected: boolean }>`
  &::before {
    border: 2px solid transparent;
  }

  display: flex;
  align-items: center;
  border-radius: 16px;
  overflow: hidden;

  height: 260px;
  width: 260px;

  ${({ selected }) => {
    return selected
      ? css`
          border: 2px solid ${({ theme }) => theme.vars.newColor.blue1};
        `
      : css`
          border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
          padding: 1px;
        `;
  }}

  &:hover {
    ${({ selected }) => {
      if (!selected) {
        return css`
          border: 1px solid ${({ theme }) => theme.vars.newColor.blue3};
        `;
      }
    }}
    cursor: pointer;
  }

  & .thumbnail-container {
    border-bottom: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
  }
`;

export function ImageSelect({
  templateName,
  createdAt,
  onClick,
  src,
  id,
  selected,
  thumbnailDimensions,
  fallback,
  badge,
  ...props
}: ImageSelectProps) {
  const context = useContext(RadioGroupContext);
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(id);
    } else {
      context.setSelectedId(id);
    }
  }, [context, onClick, id]);

  const theme = useTheme();
  const createdAtAsDate = useMemo(() => new Date(createdAt), [createdAt]);
  return (
    <Border selected={selected ?? context.selectedId === id} onClick={handleClick} {...props}>
      <Box stackItems={true}>
        <ImageArea
          src={src}
          width={258}
          height={118}
          thumbnailDimensions={thumbnailDimensions}
          fallback={fallback}
          isClickable={true}
        />
        <div style={{ padding: 16, minHeight: 140 }}>
          <Box stackItems>
            <RadioButton label={templateName} selected={selected} id={id} style={{ margin: 0 }} />
            <P
              color={theme.vars.newColor.darkGS2}
              fontSize="18px"
              style={{
                fontSize: 12,
                lineHeight: '18px',
                fontWeight: 375,
                height: 34,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Created:
              {` ${createdAtAsDate.toLocaleDateString()} at ${createdAtAsDate.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short',
              })}`}
            </P>
          </Box>
          {badge && (
            <Box style={{ marginTop: theme.designToken.spacing3 }}>
              <Badge
                size="small"
                rounded={4}
                style={{
                  color: theme.designToken.primary700,
                  backgroundColor: theme.designToken.primary50,
                  border: 'none',
                }}
              >
                {badge}
              </Badge>
            </Box>
          )}
        </div>
      </Box>
    </Border>
  );
}

ImageSelect.defaultProps = {
  src: '',
  thumbnailDimension: { width: 'auto' },
};
